import React, { useEffect, useState, Children } from 'react';
import { Menu, X, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const navItems = [{
  name: 'Home',
  href: '#home'
}, {
  name: 'How I Help',
  href: '#how-i-help'
}, {
  name: 'Process',
  href: '#process'
}, {
  name: 'Example Wins',
  href: '#wins'
}, {
  name: 'About',
  href: '#about'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      // Update active section based on scroll position
      const sections = navItems.map(item => item.href.substring(1));
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      setIsOpen(false);
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isOpen ? 'bg-[#0a0a0a]/95 backdrop-blur-lg shadow-sm border-b border-white/10' : 'bg-transparent'}`} data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        <div className="flex justify-between items-center h-20" data-id="element-199">
          {/* Logo */}
          <motion.a href="#home" onClick={e => scrollToSection(e, '#home')} className="flex items-center gap-2 font-bold text-xl text-white tracking-tight group" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-200">
            <div className="w-8 h-8 bg-gradient-to-br from-[#ff8a33] to-yellow-500 rounded-lg flex items-center justify-center text-white shadow-md group-hover:shadow-lg transition-shadow" data-id="element-201">
              <MapPin size={18} data-id="element-202" />
            </div>
            <span className="hidden sm:inline" data-id="element-203">Christopher Brissette</span>
            <span className="sm:hidden" data-id="element-204">CB</span>
          </motion.a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-1" data-id="element-205">
            {navItems.map(item => {
            const isActive = activeSection === item.href.substring(1);
            return <a key={item.name} href={item.href} onClick={e => scrollToSection(e, item.href)} className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'text-[#ff8a33]' : 'text-gray-300 hover:text-white hover:bg-white/5'}`} data-id="element-206">
                  {item.name}
                  {isActive && <motion.div layoutId="activeSection" className="absolute inset-0 bg-white/5 rounded-lg -z-10" transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30
              }} data-id="element-207" />}
                </a>;
          })}
            <motion.a href="#book-call" onClick={e => scrollToSection(e, '#book-call')} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="ml-4 px-5 py-2.5 bg-gradient-to-r from-[#ff8a33] to-yellow-500 text-white text-sm font-medium rounded-lg hover:from-[#ff9f55] hover:to-yellow-400 transition-all shadow-md hover:shadow-lg shadow-orange-500/25" data-id="element-208">
              Book a Call
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 text-gray-300 hover:text-white rounded-lg hover:bg-white/10" aria-label="Toggle menu" whileTap={{
          scale: 0.95
        }} data-id="element-209">
            {isOpen ? <X size={24} data-id="element-210" /> : <Menu size={24} data-id="element-211" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-212">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="md:hidden bg-[#0a0a0a] border-b border-white/10 overflow-hidden" data-id="element-213">
            <motion.div className="px-4 pt-2 pb-6 space-y-1" initial="closed" animate="open" exit="closed" variants={{
          open: {
            transition: {
              staggerChildren: 0.05
            }
          },
          closed: {
            transition: {
              staggerChildren: 0.05,
              staggerDirection: -1
            }
          }
        }} data-id="element-214">
              {navItems.map(item => {
            const isActive = activeSection === item.href.substring(1);
            return <motion.a key={item.name} href={item.href} onClick={e => scrollToSection(e, item.href)} variants={{
              open: {
                opacity: 1,
                x: 0
              },
              closed: {
                opacity: 0,
                x: -20
              }
            }} className={`block px-4 py-4 text-base font-medium rounded-lg transition-colors ${isActive ? 'text-[#ff8a33] bg-white/5' : 'text-gray-300 hover:text-white hover:bg-white/5'}`} data-id="element-215">
                    {item.name}
                  </motion.a>;
          })}
              <motion.div className="pt-4 px-3" variants={{
            open: {
              opacity: 1,
              x: 0
            },
            closed: {
              opacity: 0,
              x: -20
            }
          }} data-id="element-216">
                <a href="#book-call" onClick={e => scrollToSection(e, '#book-call')} className="block w-full text-center px-5 py-3 bg-gradient-to-r from-[#ff8a33] to-yellow-500 text-white font-medium rounded-lg hover:from-[#ff9f55] hover:to-yellow-400 transition-all shadow-md" data-id="element-217">
                  Book a Strategy Call
                </a>
              </motion.div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}